import React from 'react'
import { useSelector } from 'react-redux'
import { Box } from 'rebass'
import { useNewTranslation } from 'hooks'
import { theme } from 'theme'
import { useFormikContext } from 'formik'
import { EditorValues } from './types'
import { BaseEditorContainer } from '../PredefinedTaskEditor/styles'
import Text from 'particles/Text'
import Divider from 'components/Shared/Divider'
import InputField from 'components/Shared/Fields'
import styled from 'particles/themes/styled'
import _ from 'lodash'
import EmptyStepStateView from './StepsEditor/EmptyStepView'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaign } from 'state/campaigns'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { selectIsEngagementStepTypeHelper } from 'state/constants'
import { fieldIdentifiers } from 'components/Shared/Comment'

const EditorContainer = styled(BaseEditorContainer)`
    width: 100%;
    min-height: 100%;
    height: fit-content;
    box-sizing: border-box;

    & > .empty-state-wrapper {
        flex-grow: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`
interface ScheduleEditorProp {
    taskUuid: string
}

const ScheduleEditor: React.FC<ScheduleEditorProp> = ({ taskUuid }) => {
    const [t] = useNewTranslation()
    const { values } = useFormikContext<EditorValues>()
    const campaign = useSelector(selectEditingCampaign)
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)

    const { steps } = values

    return (
        <EditorContainer>
            <Text scale="H4" color="primary">
                {t('campaignEditor.createTask.taskSchedulePageTitle')}
            </Text>
            <div style={{ height: '0.5rem' }}></div>
            <Text color="secondary" scale="bodyS400">
                {t('campaignEditor.createTask.taskSchedulePageSubtitle')}
            </Text>
            <Divider maxWidth="1006px" className={'first-divider'} />

            {_.isEmpty(steps) ? (
                <div className="empty-state-wrapper">
                    <EmptyStepStateView />
                </div>
            ) : (
                steps.map((step, index: number) => {
                    return (
                        <Box key={step.uuid}>
                            <Text scale="bodyL700" color="primary">
                                {isEngagementStepType(step.type)
                                    ? t('campaignEditor.createTask.submitProofEngagementCustomTaskHeader')
                                    : t('campaignEditor.createTask.customTaskStepHeader', {
                                          stepNumber: index + 1,
                                          taskTitle: step.data.title,
                                      })}
                            </Text>

                            <Box height={theme.newSpacing.l} />
                            {campaign && (
                                <>
                                    <CampaignEditorInputFieldContainer isEditDeadline launchFieldEditable>
                                        <InputField.DatePickerField
                                            title={t('general.information.startDate')}
                                            subtitle={
                                                isEngagementStepType(step.type)
                                                    ? t('campaignEditor.createTask.enterStartDateSubmitProofSubtext')
                                                    : t('campaignEditor.createTask.enterStartDateCustomTaskSubtext')
                                            }
                                            name={`steps[${index}].time_window.start`}
                                            isRequired
                                            readOnlyForPastDate={Boolean(
                                                campaign && campaign.status === CAMPAIGN_STATUS_ENUM.LIVE,
                                            )}
                                            country={campaign.country}
                                            identifier={`${taskUuid}_${step.uuid}_${fieldIdentifiers.start}`}
                                        />
                                    </CampaignEditorInputFieldContainer>
                                    <Box height={theme.newSpacing.l} />
                                    <CampaignEditorInputFieldContainer isEditDeadline launchFieldEditable>
                                        <InputField.DatePickerField
                                            title={t('general.information.endDate')}
                                            subtitle={
                                                isEngagementStepType(step.type)
                                                    ? t('campaignEditor.createTask.enterEndDateSubmitProofSubtext')
                                                    : t('campaignEditor.createTask.enterEndDateCustomTaskSubtext')
                                            }
                                            name={`steps[${index}].time_window.end`}
                                            isRequired
                                            country={campaign.country}
                                            identifier={`${taskUuid}_${step.uuid}_${fieldIdentifiers.end}`}
                                        />
                                    </CampaignEditorInputFieldContainer>
                                </>
                            )}
                            {index + 1 !== steps.length && <Divider maxWidth="1006px" className="divider" />}
                        </Box>
                    )
                })
            )}
        </EditorContainer>
    )
}

export default ScheduleEditor
